import { useCallback, useEffect, useState } from 'react';
import {
  Col,
  Flex,
  Form,
  message,
  Row,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import { CustomParagraph, PageHeaderActions } from '../../components';
import { useToken } from '../../common/helper';
import { useSpin } from '../../components/SpinContext.jsx';
import { DeleteProduct, GetProducts } from '../../apis/onboarding';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllProducts } from '../../redux-toolkit/slice/ProductSlice.js';
import TeamBadge from '../../components/TeamBadge.jsx';
import CommonDrawer from '../../components/CommonDrawer';
import SettingsProductDrawerForm from './SettingsProductDrawerForm.jsx';
import CustomDeleteModal from '../Modals/CustomDeleteModal.jsx';
import { useMenuPermissions } from '../../hooks';
import { MenuLabels, SubMenuLabels } from '@/common/enum';
import theme from '@/assets/theme';
import CustomPagination from '../../components/CustomPagination';
import useUnauthorize from '../../hooks/useUnauthorize';
import StabilityHandlingTooltip from '../Shipments/partials/StabilityHandlingTooltip';
import { themeT } from '@/utils/themeText';
import { DeleteIcon, PencilIcon } from '@/assets/icons/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTableScroll } from '../../hooks/useTableScroll';
import './SettingsProductTable.css';

const { Paragraph } = Typography;

const SettingsProductTable = () => {
  const [productsList, setProductsList] = useState([]);
  const [form] = Form.useForm();
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  const { showSpin, hideSpin } = useSpin();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const allTeams = useSelector((state) => state.teams.list);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [requestStatus] = useState(null);
  const [deleteData, setDeleteData] = useState(null);
  const [requirementCount, setRequirementCount] = useState(0);
  const [showDrawer, setShowDrawer] = useState(false);
  const [userID, setUserID] = useState('');
  const ProductsPermissions = useMenuPermissions(
    MenuLabels.Settings,
    SubMenuLabels.Products
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [maxPageSize, setMaxPageSize] = useState();
  const [search, setSearch] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const tableScroll = useTableScroll();

  const handleCloseDrawer = () => {
    setUserID(null);
    setShowDrawer(false);
    form.resetFields();
    dispatch(fetchAllProducts({ token })); //to update store
    fetchProducts();
  };

  const handlePageSizeChange = (newPageSize) => {
    if (!newPageSize || newPageSize < 1) return;
    if (newPageSize > 200) newPageSize = 200;

    setPageSize(newPageSize);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(() => {
      const validPage = newPage > 0 ? newPage : 1;
      return validPage;
    });
  };

  // when open drawer from shipment gxp report
  useEffect(() => {
    const productId = location.state?.productId;
    if (productId) {
      setUserID(productId);
      setShowDrawer(true);
      // Clear navigation state so refresh won't reopen the drawer
      navigate(location.pathname, {
        replace: true,
        state: null,
      });
    }
  }, [location.state, location.pathname, navigate]);

  //  Api call for fetching products
  const fetchProducts = useCallback(() => {
    setProductsList([]);
    showSpin();
    GetProducts(token, page, search)
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data.data?.is_success) {
            if (Array.isArray(data.data.data) && data.data.data.length > 0) {
              setProductsList(data.data.data);
            }
            setRequirementCount(data?.data?.requirement_count ?? 0);
            setTotalPages(data.data.page_count);
            setMaxPageSize(data.data.total_count);
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  }, [hideSpin, page, search, showSpin, token, unauthorize]);

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
  };

  useEffect(() => {
    setProductsList([]);
    fetchProducts();
  }, [fetchProducts]);

  const handleFetchAllProduct = () => {};

  //if status is unauthorize
  useEffect(() => {
    if (requestStatus) {
      unauthorize(requestStatus);
    }
  }, [requestStatus, unauthorize]);

  const isLargeScreen = window.innerWidth >= 1600;
  const allColumns = [
    {
      title: 'Product name',
      dataIndex: 'name',
      key: 'name',
      width: isLargeScreen ? undefined : 200,
      render: (text) => (
        <div className='min-width-name'>
          <Typography.Text
            className='font-12 fw-600'
            ellipsis={{ tooltip: text }}
          >
            {text}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: 'Product type',
      dataIndex: 'product_name',
      key: 'product_name',
      // width: '200px',
      render: (text) => (
        <div>
          <CustomParagraph
            noText='--'
            text={text}
            fontSize={12}
            fontWeight={400}
            color='var(--text-primary)'
            marginBottom={0}
          />
        </div>
      ),
    },
    //both of these are not required for now - but may be in future.
    // {
    //   title: 'Stage of development',
    //   dataIndex: 'stage_of_development',
    //   key: 'stage_of_development',
    //   render: (text) => (
    //     <div>
    //       <CustomParagraph
    //         noText='--'
    //         text={text}
    //         fontSize={12}
    //         fontWeight={400}
    //         color='var(--text-primary)'
    //         marginBottom={0}
    //       />
    //     </div>
    //   ),
    // },
    // {
    //   title: 'Commercial value',
    //   dataIndex: 'commercial_value',
    //   key: 'commercial_value',
    //   render: (text) => (
    //     <div>
    //       <CustomParagraph
    //         noText='--'
    //         text={text}
    //         fontSize={12}
    //         fontWeight={400}
    //         color='var(--text-primary)'
    //         marginBottom={0}
    //       />
    //     </div>
    //   ),
    // },
    // project id
    {
      title: 'Project ID',
      dataIndex: 'project_id',
      key: 'project_id',
      render: (text) => (
        <div>
          <CustomParagraph
            noText='--'
            text={text}
            fontSize={12}
            fontWeight={400}
            color='var(--text-primary)'
            marginBottom={0}
          />
        </div>
      ),
    },
    // vibility window
    {
      title: 'Transit viabil...',
      dataIndex: 'viability_window',
      key: 'viability_window',
      condition: (count) => count === 0,
      render: (text) => (
        <div>
          <CustomParagraph
            noText='--'
            text={text ? `${text} hr` : '--'}
            fontSize={12}
            fontWeight={400}
            color='var(--text-primary)'
            marginBottom={0}
          />
        </div>
      ),
    },
    // temprature
    {
      title: 'Temp. range',
      dataIndex: 'temperature_range',
      key: 'temperature_range',
      condition: (count) => count == 0,
      render: (_, record) => {
        const minTemp = record.temperature_min;
        const maxTemp = record.temperature_max;
        const range = record.temperature_range || '';

        // Convert to numbers and handle NaN cases
        const minTempNum =
          minTemp !== null && minTemp !== undefined && !isNaN(Number(minTemp))
            ? Number(minTemp)
            : null;
        const maxTempNum =
          maxTemp !== null && maxTemp !== undefined && !isNaN(Number(maxTemp))
            ? Number(maxTemp)
            : null;

        return (
          <div>
            <Paragraph
              style={{
                color: 'var(--text-primary)',
                fontSize: '12px',
                fontWeight: '400',
                marginBottom: '0',
              }}
            >
              {range != 'none' &&
                minTempNum !== null &&
                maxTempNum !== null && (
                  <div>{`${Math.round(minTempNum)}°${
                    record?.temperature_unit
                  } to ${Math.round(maxTempNum)}°${record?.temperature_unit}`}</div>
                )}
              <div className='settings-product-range-label'>{`${range}`}</div>
            </Paragraph>
          </div>
        );
      },
    },
    // Stability & requirement
    {
      title: 'Requirements',
      dataIndex: 'requirements',
      key: 'requirements',
      condition: (count) => count > 0,
      render: (_, record) => {
        return (
          <div className='stability-requirement-tooltip'>
            <StabilityHandlingTooltip record={record} />
          </div>
        );
      },
    },

    // teams
    {
      title: `${themeT('team', theme.themeType)} with access`,
      dataIndex: 'teams',
      key: 'teams',
      width: isLargeScreen ? undefined : 150,
      render: (_, record) => {
        return (
          <div>
            {record?.teams?.length > 0 ? (
              <div className='flex gap-x-2 white-space-nowrap'>
                <TeamBadge
                  color={record?.teams[0]?.color}
                  text={record?.teams[0]?.name}
                  tooltipText={record?.teams[0]?.name}
                />
                {record?.teams.length > 1 && (
                  <Tooltip
                    title={record.teams
                      .slice(1)
                      .map((team) => team.name)
                      .join(', ')}
                    placement='top'
                  >
                    <div className='settings-product-team-more'>
                      <TeamBadge
                        color={null}
                        text={`+ ${record?.teams.length - 1}`}
                      />
                    </div>
                  </Tooltip>
                )}
              </div>
            ) : (
              <>
                {' '}
                <div>--</div>{' '}
              </>
            )}
          </div>
        );
      },
    },

    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      render: (text, record) => (
        <Flex align='center' gap={6}>
          {record?.permissions?.is_update && (
            <Flex
              align='center'
              justify='center'
              className='action-icon-wrapper'
              role='button'
              aria-label='Edit'
              onClick={() => handleEdit(record)}
            >
              <PencilIcon color='var(--text-primary)' />
            </Flex>
          )}
          {record?.permissions?.is_delete && (
            <>
              <Flex
                align='center'
                justify='center'
                className='action-icon-wrapper'
                role='button'
                aria-label='Delete'
                onClick={() => handleDelete(record)}
              >
                <DeleteIcon />
              </Flex>
            </>
          )}
        </Flex>
      ),
    },
  ];

  // Filter columns based on `requirementCount`
  const columns = allColumns.filter(
    (col) => !col.condition || col.condition(requirementCount)
  );

  //Table actions
  const handleDelete = (record) => {
    setDeleteData(record);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    setShowDeleteModal(false);
    if (deleteData && deleteData.id) {
      showSpin();
      DeleteProduct(deleteData.id, token)
        .then((res) => {
          const data = res?.data || null;
          if (data) {
            if (data.data?.is_success) {
              message.success(data?.data?.message);
              setDeleteData(null);
              dispatch(fetchAllProducts({ token }));
              fetchProducts();
            } else {
              message.error(data?.data?.message);
            }
          }
        })
        .catch((error) => {
          unauthorize(error);
        })
        .finally(() => {
          hideSpin();
        });
    }
  };

  const handleEdit = (record) => {
    let userId = record?.id ? record.id : '';
    setUserID(userId);
    setShowDrawer(true);
    // navigate(`/settings/product/form/${userId}`);
  };
  const totalCount = maxPageSize;
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = totalCount === 0 ? 0 : Math.min(page * pageSize, totalCount);

  const TeamsFilteredColumns =
    allTeams?.length > 0
      ? columns
      : columns.filter((column) => column.key !== 'teams');

  return (
    <>
      <div className='settings-content-wrapper'>
        <PageHeaderActions
          showToggle={false}
          title={`Products`}
          totalCount={maxPageSize}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          addButtonText={'Add'}
          onSearchChange={handleSearchChange}
          onSearchClear={handleSearchChange}
          showAddButton={ProductsPermissions?.is_create}
          onAddClick={() => {
            setShowDrawer(true);
          }}
        />

        <Table
          columns={TeamsFilteredColumns}
          dataSource={productsList}
          pagination={false}
          className='settings-table settings-table-action-cols'
          rowHoverable={false}
          scroll={tableScroll}
          tableLayout='fixed'
        />

        <Row
          style={{ width: '100%', marginTop: '20px', paddingBottom: '40px' }}
          justify='center'
        >
          <Col xs={24} className='flex-to-block'>
            <div>
              {productsList.length > 0 && (
                <Flex className='pagination-alignment'>
                  <CustomPagination
                    maxPageSize={maxPageSize}
                    page={page}
                    totalPages={totalPages}
                    pageSize={50}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                  />
                </Flex>
              )}
            </div>
          </Col>
        </Row>
      </div>
      {/* {productsList.length > 0 && (
        <Row style={{ width: "100%", marginTop: "12px" }}>
          <Col flex="auto">
            <Flex justify="flex-end">
              <CustomPagination
                maxPageSize={maxPageSize}
                page={page}
                totalPages={totalPages}
                pageSize={50}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </Flex>
          </Col>
        </Row>
      )} */}
      {/* </Space> */}
      {showDrawer && (
        <CommonDrawer open={showDrawer} onClose={() => handleCloseDrawer()}>
          <SettingsProductDrawerForm
            form={form}
            id={userID}
            handleCloseDrawer={handleCloseDrawer}
            handleFetchAllProduct={handleFetchAllProduct}
          />
        </CommonDrawer>
      )}
      <CustomDeleteModal
        visible={showDeleteModal}
        onConfirm={() => handleConfirmDelete()}
        onClose={() => setShowDeleteModal(false)}
        title={`Remove product`}
        message={`Removing this product will prevent selecting it for future use.`}
        cancelText={'Cancel'}
        confirmText={'Remove'}
        confirmColor={`#DE1135`}
      />
    </>
  );
};

export default SettingsProductTable;
